import {
  getDateValue,
  getValidEndDate,
  getValidStartDate,
  remoteRequestFailure,
  remoteRequestInitialState,
  remoteRequestLoading,
  remoteRequestSuccess,
  remoteResourceInitialState,
  remoteResourceLoadFailure,
  remoteResourceLoadSuccess,
  remoteResourceLoading,
} from '../../helpers';
import { ClosingViewMode, ClosingNotInformedCause } from '../../types';
import { generateCurrentClosing, getFilterApplied } from './helpers';
import { ActionType, Actions, State } from './types';

const initialState: State = {
  filters: {
    country: null,
    countryCode: '',
    stores: [],
    pos: [],
    salesTypes: [],
    paymentTypes: [],
    hourRange: [],
    timeBand: [],
    segments: [],
    operatorDlv: [],
    startDate: getDateValue(null),
    endDate: getDateValue(null),
    startBusinessDate: getDateValue(null),
    endBusinessDate: getDateValue(null),
    startOperationDate: getDateValue(null),
    endOperationDate: getDateValue(null),
    startAccountingDate: getDateValue(null),
    endAccountingDate: getDateValue(null),
    groupBy: ['countryAcronym', 'storeAcronym', 'accountingDate'],
    discountsJoin: [],
    tenderGroupBy: ['countryAcronym', 'tenderName'],
    salesProductGroupBy: ['countryAcronym', 'storeAcronym', 'businessDate'],
    thirdPartySalesByItemsGroupBy: ['countryAcronym', 'storeAcronym', 'plu'],
    economicAccountingGroupBy: ['countryAcronym'],
    operationType: [],
    transactionType: [],
    transactionTypeByProduct: [],
    plu: [],
    pod: [],
    saleDiscount: [],
    itemDiscount: [],
    differenceType: '',
    accountingEntriesFields: false,
    onlyDifferences: false,
    franchiseType: [],
  },
  filterApplied: [],
  opStructuresFilters: {
    agreement: [],
    regionalMgmt: [],
    region: [],
    management: [],
    supervision: [],
    company: [],
  },
  gcsFilters: {
    filterApplied: [],
    startBusinessDate: getDateValue(null),
    endBusinessDate: getDateValue(null),
    startOperationDate: getDateValue(null),
    endOperationDate: getDateValue(null),
    startAccountingDate: getDateValue(null),
    endAccountingDate: getDateValue(null),
  },
  collectionsFilters: {
    groupBy: ['countryAcronym', 'storeAcronym'],
    startBusinessDate: getDateValue(null),
    endBusinessDate: getDateValue(null),
    movementType: [],
  },
  collectionsEconomicAccountingFilters: {
    groupBy: ['countryAcronym', 'paymentTypeId', 'account', 'subaccount'],
    startBusinessDate: getDateValue(null),
    endBusinessDate: getDateValue(null),
  },
  accountingInformationFilters: {
    startBusinessDate: getDateValue(null),
    endBusinessDate: getDateValue(null),
  },
  cancellationsFilters: {
    groupBy: ['countryAcronym'],
    startAccountingDate: getDateValue(null),
    endAccountingDate: getDateValue(null),
  },
  mastersImportFilters: {
    countries: [],
    startImportDate: getDateValue(null),
    endImportDate: getDateValue(null),
    state: [],
  },
  closingFilters: {
    countries: [],
    stores: [],
    startBusinessDate: getDateValue(null),
    endBusinessDate: getDateValue(null),
    viewMode: ClosingViewMode.CONSOLIDATED,
    franchiseTypes: [],
    federativeEntities: [],
    causeTypes: [],
  },
  posOptions: remoteResourceInitialState([]),
  salesTypesOptions: remoteResourceInitialState([]),
  operatorDlvOptions: remoteResourceInitialState([]),
  sales: remoteResourceInitialState([]),
  gcs: remoteResourceInitialState([]),
  gcsPaymentTypes: remoteResourceInitialState([]),
  gcsByTax: remoteResourceInitialState([]),
  tender: remoteResourceInitialState([]),
  operationTypeOptions: remoteResourceInitialState([]),
  transactionTypeOptions: remoteResourceInitialState([]),
  salesByProduct: remoteResourceInitialState([]),
  gcsByProduct: remoteResourceInitialState([]),
  thirdPartySalesByItems: remoteResourceInitialState([]),
  causeTypesOptions: remoteResourceInitialState([]),
  closing: {
    sales: remoteResourceInitialState([]),
    collections: remoteResourceInitialState([]),
    current: [],
  },
  missingClosingCauses: {
    salesCauses: remoteResourceInitialState({} as never),
    collectionsCauses: remoteResourceInitialState({} as never),
  },
  missingControlErrors: {
    controlErrors: remoteResourceLoadSuccess<ClosingNotInformedCause | undefined>(undefined),
  },
  causesFilters: {
    country: '',
    store: '',
    date: getDateValue(null),
    cause: '',
  },
  missingClosingTotal: {
    filteredStores: [],
    missingClosingTotal: remoteResourceInitialState(null),
  },
  hourRangeOptions: remoteResourceInitialState([]),
  timeBandOptions: remoteResourceInitialState([]),
  reliefs: remoteResourceInitialState([]),
  paymentTypeOptions: remoteResourceInitialState([]),
  cancellations: remoteResourceInitialState([]),
  saleDiscountOptions: remoteResourceInitialState([]),
  itemDiscountOptions: remoteResourceInitialState([]),
  pettyCashDifference: remoteResourceInitialState([]),
  pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
  economicAccountingReport: remoteResourceInitialState([]),
  cashMovementReport: remoteResourceInitialState([]),
  cashCountReport: remoteResourceInitialState([]),
  cashDiscrepancyReport: remoteResourceInitialState([]),
  exchangePurchaseReport: remoteResourceInitialState([]),
  fixedFundReport: remoteResourceInitialState([]),
  pettyCashWithdrawalReport: remoteResourceInitialState([]),
  extraCashReport: remoteResourceInitialState([]),
  recoveryReport: remoteResourceInitialState([]),
  envelopeDetailsReport: remoteResourceInitialState([]),
  salesJournal: {
    gcs: remoteResourceInitialState([]),
    ncs: remoteResourceInitialState([]),
    fcs: remoteResourceInitialState([]),
    thirdparty: remoteResourceInitialState([]),
  },
  mastersImport: remoteResourceInitialState([]),
  mastersTypes: remoteResourceInitialState([]),
  mastersImportExecution: remoteRequestInitialState,
  interfacesFilter: {
    country: '',
    stores: [],
    startDate: getDateValue(null),
    endDate: getDateValue(null),
    status: '',
    interfaceName: '',
  },
  interfaces: remoteResourceInitialState([]),
  interfacesParameters: remoteResourceInitialState(undefined),
  pendingInterfacesFilter: {
    country: '',
    stores: [],
    startBusinessDate: getDateValue(null),
    endBusinessDate: getDateValue(null),
    queryFilter: 'Todos los locales',
  },
  pendingInterfacesState: {
    sales: remoteResourceInitialState([]),
  },
  pendingInterfaces: remoteResourceInitialState([]),
  executionStatusPendingInterfacesFilter: {
    country: '',
    stores: [],
    businessDate: getDateValue(null),
    status: 'PENDING',
  },
  executionStatusPendingInterfacesState: {
    pendingInterfacesStates: remoteResourceInitialState([]),
  },
  executionStatusPendingInterfaces: remoteResourceInitialState([]),
  parameters: remoteResourceInitialState(undefined),
};

const reducer = (state: State = initialState, action: Actions): State => {
  switch (action.type) {
    case ActionType.changeFiltersCountry:
      return {
        ...state,
        posOptions: remoteResourceInitialState([]),
        operatorDlvOptions: remoteResourceInitialState([]),
        salesTypesOptions: remoteResourceInitialState([]),
        timeBandOptions: remoteResourceInitialState([]),
        paymentTypeOptions: remoteResourceInitialState([]),
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        reliefs: remoteResourceInitialState([]),
        cancellations: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        thirdPartySalesByItems: remoteResourceInitialState([]),
        economicAccountingReport: remoteResourceInitialState([]),
        transactionTypeOptions: remoteResourceInitialState([]),
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        envelopeDetailsReport: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{
            countryCode: action.countryCode,
            stores: [],
            pos: [],
            salesTypes: [],
            hourRange: [],
            timeBand: [],
            segments: [],
            paymentTypes: [],
            pod: [],
            operatorDlv: [],
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'countryCode', action.countryCode),
        opStructuresFilters: {
          agreement: [],
          regionalMgmt: [],
          region: [],
          management: [],
          supervision: [],
          company: [],
        },
      };
    case ActionType.changeFiltersStore:
      return {
        ...state,
        posOptions: remoteResourceInitialState([]),
        operatorDlvOptions: remoteResourceInitialState([]),
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        reliefs: remoteResourceInitialState([]),
        cancellations: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        thirdPartySalesByItems: remoteResourceInitialState([]),
        economicAccountingReport: remoteResourceInitialState([]),
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        envelopeDetailsReport: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ stores: action.stores, pos: [], operatorDlv: [] },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'stores', action.stores),
      };
    case ActionType.changeFiltersPos:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ pos: action.pos },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'pos', action.pos),
      };
    case ActionType.changeFiltersSalesTypes:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ salesTypes: action.salesTypes },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'salesTypes', action.salesTypes),
      };
    case ActionType.changeFiltersPaymentTypes:
      return {
        ...state,
        tender: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        economicAccountingReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ paymentTypes: action.paymentTypes },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'paymentTypes', action.paymentTypes),
      };
    case ActionType.changeFiltersOperationType:
      return {
        ...state,
        tender: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ operationType: action.operationType },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'operationType', action.operationType),
      };
    case ActionType.changeFiltersTransactionType:
      return {
        ...state,
        tender: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ transactionType: action.transactionType },
        },
        filterApplied: getFilterApplied(
          state.filterApplied,
          'transactionType',
          action.transactionType
        ),
      };

    case ActionType.changeFiltersTransactionTypeByProduct:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...{ transactionTypeByProduct: action.transactionType },
        },
        filterApplied: getFilterApplied(
          state.filterApplied,
          'transactionTypeByProduct',
          action.transactionType
        ),
      };

    case ActionType.changeFiltersTenderName:
      return {
        ...state,
        tender: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ tenderName: action.tenderName },
        },
      };
    case ActionType.changeFiltersOperatorDlv:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ operatorDlv: action.operatorDlv },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'operatorDlv', action.operatorDlv),
      };
    case ActionType.changeFiltersHourRange:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ hourRange: action.hourRange, timeBand: [] },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'hourRange', action.hourRange),
      };
    case ActionType.changeFiltersSaleDiscount:
      return {
        ...state,
        gcs: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ saleDiscount: action.discount },
        },
      };
    case ActionType.changeFiltersItemDiscount:
      return {
        ...state,
        gcs: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ itemDiscount: action.discount },
        },
      };
    case ActionType.changeFiltersDifferenceType:
      return {
        ...state,
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ differenceType: action.differenceType },
        },
      };
    case ActionType.changeFiltersPlu:
      return {
        ...state,
        salesByProduct: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        thirdPartySalesByItems: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ plu: action.plu },
        },
      };
    case ActionType.changeFiltersStoreMotherChild:
      return {
        ...state,
        salesByProduct: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
        thirdPartySalesByItems: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ pod: action.pod },
        },
      };
    case ActionType.changeFiltersTimeBand:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ timeBand: action.timeBand, hourRange: [] },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'timeBand', action.timeBand),
      };
    case ActionType.changeFiltersSegments:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ segments: action.segments },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'segments', action.segments),
      };
    case ActionType.changeFilterStartBusinessDate:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceInitialState(null),
        },
        tender: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{
            startBusinessDate: getDateValue(action.date),
            endBusinessDate: getDateValue(
              getValidEndDate(action.date, state.filters.endBusinessDate)
            ),
          },
        },
        closing: {
          ...state.closing,
          ...{
            sales: remoteResourceInitialState([]),
            collections: remoteResourceInitialState([]),
            current: [],
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'startBusinessDate', action.date),
      };
    case ActionType.changeFilterEndBusinessDate:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceInitialState(null),
        },
        tender: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{
            endBusinessDate: getDateValue(action.date),
            startBusinessDate: getDateValue(
              getValidStartDate(state.filters.startBusinessDate, action.date)
            ),
          },
        },
        closing: {
          ...state.closing,
          ...{
            sales: remoteResourceInitialState([]),
            collections: remoteResourceInitialState([]),
            current: [],
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'endBusinessDate', action.date),
      };
    case ActionType.changeFilterStartOperationDate:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceInitialState(null),
        },
        tender: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{
            startOperationDate: getDateValue(action.date),
            endOperationDate: getDateValue(
              getValidEndDate(action.date, state.filters.endOperationDate)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'startOperationDate', action.date),
      };
    case ActionType.changeFilterEndOperationDate:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceInitialState(null),
        },
        tender: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{
            endOperationDate: getDateValue(action.date),
            startOperationDate: getDateValue(
              getValidStartDate(state.filters.startOperationDate, action.date)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'endOperationDate', action.date),
      };
    case ActionType.changeFilterStartAccountingDate:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceInitialState(null),
        },
        tender: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{
            startAccountingDate: getDateValue(action.date),
            endAccountingDate: getDateValue(
              getValidEndDate(action.date, state.filters.endAccountingDate)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'startAccountingDate', action.date),
      };
    case ActionType.changeFilterEndAccountingDate:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceInitialState(null),
        },
        tender: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{
            endAccountingDate: getDateValue(action.date),
            startAccountingDate: getDateValue(
              getValidStartDate(state.filters.startAccountingDate, action.date)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'endAccountingDate', action.date),
      };
    case ActionType.changeFilterGroupBy:
      return {
        ...state,
        sales:
          state.filters.groupBy === action.groupBy ? state.sales : remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ groupBy: action.groupBy },
        },
      };
    case ActionType.changeGcsFilterStartBusinessDate:
      return {
        ...state,
        gcsFilters: {
          ...state.gcsFilters,
          startBusinessDate: getDateValue(action.date),
          endBusinessDate: getDateValue(
            getValidEndDate(action.date, state.gcsFilters.endBusinessDate)
          ),
          filterApplied: getFilterApplied(
            state.gcsFilters.filterApplied,
            'startBusinessDate',
            action.date
          ),
        },
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
      };
    case ActionType.changeGcsFilterEndBusinessDate:
      return {
        ...state,
        gcsFilters: {
          ...state.gcsFilters,
          endBusinessDate: getDateValue(action.date),
          startBusinessDate: getDateValue(
            getValidStartDate(state.gcsFilters.startBusinessDate, action.date)
          ),
          filterApplied: getFilterApplied(
            state.gcsFilters.filterApplied,
            'endBusinessDate',
            action.date
          ),
        },
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
      };
    case ActionType.changeGcsFilterStartOperationDate:
      return {
        ...state,
        gcsFilters: {
          ...state.gcsFilters,
          startOperationDate: getDateValue(action.date),
          endOperationDate: getDateValue(
            getValidEndDate(action.date, state.gcsFilters.endOperationDate)
          ),
          filterApplied: getFilterApplied(
            state.gcsFilters.filterApplied,
            'startOperationDate',
            action.date
          ),
        },
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
      };
    case ActionType.changeGcsFilterEndOperationDate:
      return {
        ...state,
        gcsFilters: {
          ...state.gcsFilters,
          endOperationDate: getDateValue(action.date),
          startOperationDate: getDateValue(
            getValidStartDate(state.gcsFilters.startOperationDate, action.date)
          ),
          filterApplied: getFilterApplied(
            state.gcsFilters.filterApplied,
            'endOperationDate',
            action.date
          ),
        },
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
      };
    case ActionType.changeGcsFilterStartAccountingDate:
      return {
        ...state,
        gcsFilters: {
          ...state.gcsFilters,
          startAccountingDate: getDateValue(action.date),
          endAccountingDate: getDateValue(
            getValidEndDate(action.date, state.gcsFilters.endAccountingDate)
          ),
          filterApplied: getFilterApplied(
            state.gcsFilters.filterApplied,
            'startAccountingDate',
            action.date
          ),
        },
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
      };
    case ActionType.changeGcsFilterEndAccountingDate:
      return {
        ...state,
        gcsFilters: {
          ...state.gcsFilters,
          endAccountingDate: getDateValue(action.date),
          startAccountingDate: getDateValue(
            getValidStartDate(state.gcsFilters.startAccountingDate, action.date)
          ),
          filterApplied: getFilterApplied(
            state.gcsFilters.filterApplied,
            'endAccountingDate',
            action.date
          ),
        },
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        gcsByProduct: remoteResourceInitialState([]),
      };
    case ActionType.changeCollectionsFilterGroupBy:
      return {
        ...state,
        reliefs:
          state.collectionsFilters.groupBy === action.groupBy
            ? state.reliefs
            : remoteResourceInitialState([]),
        collectionsFilters: {
          ...state.collectionsFilters,
          ...{ groupBy: action.groupBy },
        },
      };
    case ActionType.changeCollectionsEconomicAccountingFilterGroupBy:
      return {
        ...state,
        economicAccountingReport:
          state.collectionsEconomicAccountingFilters.groupBy === action.groupBy
            ? state.economicAccountingReport
            : remoteResourceInitialState([]),
        collectionsEconomicAccountingFilters: {
          ...state.collectionsEconomicAccountingFilters,
          ...{ groupBy: action.groupBy },
        },
      };
    case ActionType.changeAccountingInformationFilterStartBusinessDate: {
      const startBusinessDate = getDateValue(action.date);
      const endBusinessDate = getDateValue(
        getValidEndDate(action.date, state.accountingInformationFilters.endBusinessDate)
      );
      return {
        ...state,
        accountingInformationFilters: {
          startBusinessDate,
          endBusinessDate,
        },
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
      };
    }
    case ActionType.changeAccountingInformationFilterEndBusinessDate: {
      const endBusinessDate = getDateValue(action.date);
      const startBusinessDate = getDateValue(
        getValidStartDate(state.accountingInformationFilters.startBusinessDate, action.date)
      );
      return {
        ...state,
        accountingInformationFilters: {
          startBusinessDate,
          endBusinessDate,
        },
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
      };
    }
    case ActionType.changeCollectionsEconomicAccountingFilterStartBusinessDate: {
      const startBusinessDate = getDateValue(action.date);
      const endBusinessDate = getDateValue(
        getValidEndDate(action.date, state.collectionsEconomicAccountingFilters.endBusinessDate)
      );
      return {
        ...state,
        economicAccountingReport: remoteResourceInitialState([]),
        collectionsFilters: {
          ...state.collectionsFilters,
          startBusinessDate,
          endBusinessDate,
        },
        collectionsEconomicAccountingFilters: {
          ...state.collectionsEconomicAccountingFilters,
          startBusinessDate,
          endBusinessDate,
        },
      };
    }
    case ActionType.changeCollectionsEconomicAccountingFilterEndBusinessDate: {
      const endBusinessDate = getDateValue(action.date);
      const startBusinessDate = getDateValue(
        getValidStartDate(state.collectionsEconomicAccountingFilters.startBusinessDate, action.date)
      );
      return {
        ...state,
        collectionsEconomicAccountingFilters: {
          ...state.collectionsEconomicAccountingFilters,
          startBusinessDate,
          endBusinessDate,
        },
      };
    }
    case ActionType.changeCollectionsFilterStartBusinessDate: {
      const startBusinessDate = getDateValue(action.date);
      const endBusinessDate = getDateValue(
        getValidEndDate(action.date, state.collectionsFilters.endBusinessDate)
      );
      return {
        ...state,
        economicAccountingReport: remoteResourceInitialState([]),
        envelopeDetailsReport: remoteResourceInitialState([]),
        collectionsFilters: {
          ...state.collectionsFilters,
          startBusinessDate,
          endBusinessDate,
        },
        collectionsEconomicAccountingFilters: {
          ...state.collectionsEconomicAccountingFilters,
          startBusinessDate,
          endBusinessDate,
        },
        reliefs: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
      };
    }
    case ActionType.changeCollectionsFilterEndBusinessDate: {
      const endBusinessDate = getDateValue(action.date);
      const startBusinessDate = getDateValue(
        getValidStartDate(state.collectionsFilters.startBusinessDate, action.date)
      );
      return {
        ...state,
        economicAccountingReport: remoteResourceInitialState([]),
        envelopeDetailsReport: remoteResourceInitialState([]),
        collectionsFilters: {
          ...state.collectionsFilters,
          startBusinessDate,
          endBusinessDate,
        },
        collectionsEconomicAccountingFilters: {
          ...state.collectionsEconomicAccountingFilters,
          startBusinessDate,
          endBusinessDate,
        },
        reliefs: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
      };
    }
    case ActionType.changeCollectionsFilterMovementsType:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
          movementType: action.movementsType,
        },
        reliefs: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
      };
    case ActionType.changeCancellationsFilterGroupBy:
      return {
        ...state,
        cancellations:
          state.cancellationsFilters.groupBy === action.groupBy
            ? state.cancellations
            : remoteResourceInitialState([]),
        cancellationsFilters: {
          ...state.cancellationsFilters,
          ...{ groupBy: action.groupBy },
        },
      };
    case ActionType.changeCancellationsFilterStartAccountingDate:
      return {
        ...state,
        cancellationsFilters: {
          ...state.cancellationsFilters,
          startAccountingDate: getDateValue(action.date),
          endAccountingDate: getDateValue(
            getValidEndDate(action.date, state.cancellationsFilters.endAccountingDate)
          ),
        },
        cancellations: remoteResourceInitialState([]),
      };
    case ActionType.changeCancellationsFilterEndAccountingDate:
      return {
        ...state,
        cancellationsFilters: {
          ...state.cancellationsFilters,
          endAccountingDate: getDateValue(action.date),
          startAccountingDate: getDateValue(
            getValidStartDate(state.cancellationsFilters.startAccountingDate, action.date)
          ),
        },
        cancellations: remoteResourceInitialState([]),
      };
    case ActionType.changeFilterThirdPartySalesByItemsGroupBy:
      return {
        ...state,
        thirdPartySalesByItems:
          state.filters.thirdPartySalesByItemsGroupBy === action.groupBy
            ? state.thirdPartySalesByItems
            : remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ thirdPartySalesByItemsGroupBy: action.groupBy },
        },
      };
    case ActionType.changeFiltersTenderGroupBy:
      return {
        ...state,
        tender:
          state.filters.tenderGroupBy === action.groupBy
            ? state.tender
            : remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ tenderGroupBy: action.groupBy },
        },
      };
    case ActionType.changeSaleProductFilterGroupBy:
      return {
        ...state,
        salesByProduct:
          state.filters.salesProductGroupBy === action.groupBy
            ? state.salesByProduct
            : remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          ...{ salesProductGroupBy: action.groupBy },
        },
      };
    case ActionType.changeFiltersDiscountsJoin:
      return {
        ...state,
        gcsByProduct:
          state.filters.discountsJoin === action.discountsJoin
            ? state.gcsByProduct
            : remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          discountsJoin: action.discountsJoin,
        },
      };
    case ActionType.findPosRequested:
      return {
        ...state,
        posOptions: remoteResourceLoading([]),
      };
    case ActionType.findPosSuccess:
      return {
        ...state,
        posOptions: remoteResourceLoadSuccess(action.posOptions),
      };
    case ActionType.findPosFailure:
      return {
        ...state,
        posOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findOperatorDlvRequested:
      return {
        ...state,
        operatorDlvOptions: remoteResourceLoading([]),
      };
    case ActionType.findOperatorDlvSuccess:
      return {
        ...state,
        operatorDlvOptions: remoteResourceLoadSuccess(action.operatorDlvOptions),
      };
    case ActionType.findOperatorDlvFailure:
      return {
        ...state,
        operatorDlvOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findSaleDiscountsRequested:
      return {
        ...state,
        saleDiscountOptions: remoteResourceLoading([]),
      };
    case ActionType.findSaleDiscountsSuccess:
      return {
        ...state,
        saleDiscountOptions: remoteResourceLoadSuccess(action.discountOptions),
      };
    case ActionType.findSaleDiscountsFailure:
      return {
        ...state,
        saleDiscountOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findItemDiscountsRequested:
      return {
        ...state,
        itemDiscountOptions: remoteResourceLoading([]),
      };
    case ActionType.findItemDiscountsSuccess:
      return {
        ...state,
        itemDiscountOptions: remoteResourceLoadSuccess(action.discountOptions),
      };
    case ActionType.findItemDiscountsFailure:
      return {
        ...state,
        itemDiscountOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findSalesTypesRequested:
      return {
        ...state,
        salesTypesOptions: remoteResourceLoading([]),
      };
    case ActionType.findSalesTypesSuccess:
      return {
        ...state,
        salesTypesOptions: remoteResourceLoadSuccess(action.salesTypesOptions),
      };
    case ActionType.findSalesTypesFailure:
      return {
        ...state,
        salesTypesOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findCauseTypesRequested:
      return {
        ...state,
        causeTypesOptions: remoteResourceLoading([]),
      };
    case ActionType.findCauseTypesSuccess:
      return {
        ...state,
        causeTypesOptions: remoteResourceLoadSuccess(action.causeTypesOptions),
      };
    case ActionType.findCauseTypesFailure:
      return {
        ...state,
        causeTypesOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findOperationTypesRequested:
      return {
        ...state,
        operationTypeOptions: remoteResourceLoading([]),
      };
    case ActionType.findOperationTypesSuccess:
      return {
        ...state,
        operationTypeOptions: remoteResourceLoadSuccess(action.operationTypes),
      };
    case ActionType.findOperationTypesFailure:
      return {
        ...state,
        operationTypeOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findTransactionTypeRequested:
      return {
        ...state,
        transactionTypeOptions: remoteResourceLoading([]),
      };
    case ActionType.findTransactionTypeSuccess:
      return {
        ...state,
        transactionTypeOptions: remoteResourceLoadSuccess(action.transactionType),
      };
    case ActionType.findTransactionTypeFailure:
      return {
        ...state,
        transactionTypeOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findPaymentTypeRequested:
      return {
        ...state,
        paymentTypeOptions: remoteResourceLoading([]),
      };
    case ActionType.findPaymentTypeSuccess:
      return {
        ...state,
        paymentTypeOptions: remoteResourceLoadSuccess(action.paymentType),
      };
    case ActionType.findPaymentTypeFailure:
      return {
        ...state,
        paymentTypeOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findHourRangeRequested:
      return {
        ...state,
        hourRangeOptions: remoteResourceLoading([]),
      };
    case ActionType.findHourRangeSuccess:
      return {
        ...state,
        hourRangeOptions: remoteResourceLoadSuccess(action.hourRange),
      };
    case ActionType.findHourRangeFailure:
      return {
        ...state,
        hourRangeOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findTimeBandRequested:
      return {
        ...state,
        timeBandOptions: remoteResourceLoading([]),
      };
    case ActionType.findTimeBandSuccess:
      return {
        ...state,
        timeBandOptions: remoteResourceLoadSuccess(action.timeBand),
      };
    case ActionType.findTimeBandFailure:
      return {
        ...state,
        timeBandOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findSalesRequested:
      return {
        ...state,
        sales: remoteResourceLoading([]),
      };
    case ActionType.findSalesSuccess:
      return {
        ...state,
        sales: remoteResourceLoadSuccess(action.sales),
      };
    case ActionType.findSalesFailure:
      return {
        ...state,
        sales: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findReportTenderRequested:
      return {
        ...state,
        tender: remoteResourceLoading([]),
      };
    case ActionType.findReportTenderSuccess:
      return {
        ...state,
        tender: remoteResourceLoadSuccess(action.reportTender),
      };
    case ActionType.findReportTenderFailure:
      return {
        ...state,
        tender: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findGcsRequested:
      return {
        ...state,
        gcs: remoteResourceLoading([]),
      };
    case ActionType.findGcsSuccess:
      return {
        ...state,
        gcs: remoteResourceLoadSuccess(action.gcs),
      };
    case ActionType.findGcsFailure:
      return {
        ...state,
        gcs: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findThirdPartySalesByItemsRequested:
      return {
        ...state,
        thirdPartySalesByItems: remoteResourceLoading([]),
      };
    case ActionType.findThirdPartySalesByItemsSuccess:
      return {
        ...state,
        thirdPartySalesByItems: remoteResourceLoadSuccess(action.thirdPartySalesByItems),
      };
    case ActionType.findThirdPartySalesByItemsFailure:
      return {
        ...state,
        thirdPartySalesByItems: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findGcsByPaymentTypesRequested:
      return {
        ...state,
        gcsPaymentTypes: remoteResourceLoading([]),
      };
    case ActionType.findGcsByPaymentTypesSuccess:
      return {
        ...state,
        gcsPaymentTypes: remoteResourceLoadSuccess(action.gcsPaymentTypes),
      };
    case ActionType.findGcsByPaymentTypesFailure:
      return {
        ...state,
        gcsPaymentTypes: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findGcsByTaxRequested:
      return {
        ...state,
        gcsByTax: remoteResourceLoading([]),
      };
    case ActionType.findGcsByTaxSuccess:
      return {
        ...state,
        gcsByTax: remoteResourceLoadSuccess(action.gcsTax),
      };
    case ActionType.findGcsByTaxFailure:
      return {
        ...state,
        gcsByTax: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findClosingSalesRequested:
      return {
        ...state,
        closing: {
          ...state.closing,
          ...{ sales: remoteResourceLoading([]), current: [] },
        },
      };
    case ActionType.findClosingSalesSuccess:
      return {
        ...state,
        closing: {
          ...state.closing,
          ...{
            sales: remoteResourceLoadSuccess(action.closing),
            current: generateCurrentClosing(
              action.closing,
              action.startDate,
              action.endDate,
              state.closingFilters.viewMode,
              state.closing.collections
            ),
          },
        },
      };
    case ActionType.findClosingSalesFailure:
      return {
        ...state,
        closing: {
          ...state.closing,
          ...{ sales: remoteResourceLoadFailure([], action.error), current: [] },
        },
      };
    case ActionType.findClosingCollectionsRequested:
      return {
        ...state,
        closing: {
          ...state.closing,
          ...{ collections: remoteResourceLoading([]), current: [] },
        },
      };
    case ActionType.findClosingCollectionsSuccess:
      return {
        ...state,
        closing: {
          ...state.closing,
          ...{
            collections: remoteResourceLoadSuccess(action.closing),
            current: generateCurrentClosing(
              action.closing,
              action.startDate,
              action.endDate,
              state.closingFilters.viewMode,
              state.closing.sales
            ),
          },
        },
      };
    case ActionType.findClosingCollectionsFailure:
      return {
        ...state,
        closing: {
          ...state.closing,
          ...{ collections: remoteResourceLoadFailure([], action.error), current: [] },
        },
      };
    case ActionType.findMissingClosingSalesCausesRequested:
      return {
        ...state,
        missingClosingCauses: {
          ...state.missingClosingCauses,
          salesCauses: remoteResourceLoading({} as never),
        },
      };
    case ActionType.findMissingClosingSalesCausesSuccess:
      return {
        ...state,
        missingClosingCauses: {
          ...state.missingClosingCauses,
          salesCauses: remoteResourceLoadSuccess(action.missingClosingCauses),
        },
      };
    case ActionType.findMissingClosingSalesCausesFailure:
      return {
        ...state,
        missingClosingCauses: {
          ...state.missingClosingCauses,
          salesCauses: remoteResourceLoadFailure({} as never, action.error),
        },
      };
    case ActionType.findMissingClosingCollectionsCausesRequested:
      return {
        ...state,
        missingClosingCauses: {
          ...state.missingClosingCauses,
          collectionsCauses: remoteResourceLoading({} as never),
        },
      };
    case ActionType.findMissingClosingCollectionsCausesSuccess:
      return {
        ...state,
        missingClosingCauses: {
          ...state.missingClosingCauses,
          collectionsCauses: remoteResourceLoadSuccess(action.missingClosingCauses),
        },
      };
    case ActionType.findMissingClosingCollectionsCausesFailure:
      return {
        ...state,
        missingClosingCauses: {
          ...state.missingClosingCauses,
          collectionsCauses: remoteResourceLoadFailure({} as never, action.error),
        },
      };
    case ActionType.findMissingControlErrorsRequested:
      return {
        ...state,
        missingControlErrors: {
          ...state.missingControlErrors,
          controlErrors: remoteResourceLoading({} as never),
        },
      };
    case ActionType.findMissingControlErrorsSuccess:
      return {
        ...state,
        missingControlErrors: {
          ...state.missingControlErrors,
          controlErrors: remoteResourceLoadSuccess(action.missingControlErrors),
        },
      };
    case ActionType.findMissingControlErrorsFailure:
      return {
        ...state,
        missingControlErrors: {
          ...state.missingControlErrors,
          controlErrors: remoteResourceLoadFailure({} as never, action.error),
        },
      };
    case ActionType.changeCausesFilterCountry:
      return {
        ...state,
        causesFilters: {
          ...state.causesFilters,
          ...{
            country: action.country,
            store: '',
            date: getDateValue(null),
            cause: '',
          },
        },
      };
    case ActionType.changeCausesFilterStore:
      return {
        ...state,
        causesFilters: {
          ...state.causesFilters,
          ...{
            store: action.store,
          },
        },
      };
    case ActionType.changeCausesFilterDate:
      return {
        ...state,
        causesFilters: {
          ...state.causesFilters,
          ...{
            date: getDateValue(action.date),
          },
        },
      };
    case ActionType.changeCausesFilterCause:
      return {
        ...state,
        causesFilters: {
          ...state.causesFilters,
          ...{
            cause: action.cause,
          },
        },
      };
    case ActionType.changeOpStrucuresFilterAgreement:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        reliefs: remoteResourceInitialState([]),
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ agreement: action.agreement },
        },
      };
    case ActionType.changeOpStrucuresFilterRegionalManagement:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        reliefs: remoteResourceInitialState([]),
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ regionalMgmt: action.regionalMgmt },
        },
      };
    case ActionType.changeOpStrucuresFilterRegion:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        reliefs: remoteResourceInitialState([]),
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ region: action.region },
        },
      };
    case ActionType.changeOpStrucuresFilterManagement:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        reliefs: remoteResourceInitialState([]),
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ management: action.management },
        },
      };
    case ActionType.changeOpStrucuresFilterSupervision:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        reliefs: remoteResourceInitialState([]),
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ supervision: action.supervision },
        },
      };
    case ActionType.changeOpStrucuresFilterCompany:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        gcs: remoteResourceInitialState([]),
        gcsPaymentTypes: remoteResourceInitialState([]),
        gcsByTax: remoteResourceInitialState([]),
        tender: remoteResourceInitialState([]),
        salesByProduct: remoteResourceInitialState([]),
        cashCountReport: remoteResourceInitialState([]),
        cashDiscrepancyReport: remoteResourceInitialState([]),
        exchangePurchaseReport: remoteResourceInitialState([]),
        fixedFundReport: remoteResourceInitialState([]),
        pettyCashWithdrawalReport: remoteResourceInitialState([]),
        recoveryReport: remoteResourceInitialState([]),
        extraCashReport: remoteResourceInitialState([]),
        cashMovementReport: remoteResourceInitialState([]),
        pettyCashDifference: remoteResourceInitialState([]),
        pettyCashDifferenceByPaymentType: remoteResourceInitialState([]),
        reliefs: remoteResourceInitialState([]),
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ company: action.company },
        },
      };
    case ActionType.findMissingClosingTotalRequested:
      return {
        ...state,
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceLoading(null),
        },
      };
    case ActionType.findMissingClosingTotalSuccess:
      return {
        ...state,
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceLoadSuccess(action.totals),
        },
      };
    case ActionType.findMissingClosingTotalFailure:
      return {
        ...state,
        missingClosingTotal: {
          ...state.missingClosingTotal,
          missingClosingTotal: remoteResourceLoadFailure(null, action.error),
        },
      };
    case ActionType.changeMissingClosingTotalFilteredStores:
      return {
        ...state,
        missingClosingTotal: {
          ...state.missingClosingTotal,
          filteredStores: action.stores,
        },
      };
    case ActionType.findReliefsRequested:
      return {
        ...state,
        reliefs: remoteResourceLoading([]),
      };
    case ActionType.findReliefsSuccess:
      return {
        ...state,
        reliefs: remoteResourceLoadSuccess(action.reliefs),
      };
    case ActionType.findReliefsFailure:
      return {
        ...state,
        reliefs: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findCancellationsRequested:
      return {
        ...state,
        cancellations: remoteResourceLoading([]),
      };
    case ActionType.findCancellationsSuccess:
      return {
        ...state,
        cancellations: remoteResourceLoadSuccess(action.cancellations),
      };
    case ActionType.findCancellationsFailure:
      return {
        ...state,
        cancellations: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findSalesByProductRequested:
      return {
        ...state,
        salesByProduct: remoteResourceLoading([]),
      };
    case ActionType.findSalesByProductSuccess:
      return {
        ...state,
        salesByProduct: remoteResourceLoadSuccess(action.salesByProduct),
      };
    case ActionType.findSalesByProductFailure:
      return {
        ...state,
        salesByProduct: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findGCsByProductRequested:
      return {
        ...state,
        gcsByProduct: remoteResourceLoading([]),
      };
    case ActionType.findGCsByProductSuccess:
      return {
        ...state,
        gcsByProduct: remoteResourceLoadSuccess(action.gcsByProduct),
      };
    case ActionType.findGCsByProductFailure:
      return {
        ...state,
        gcsByProduct: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findPettyCashDifferenceRequested:
      return {
        ...state,
        pettyCashDifference: remoteResourceLoading([]),
      };
    case ActionType.findPettyCashDifferenceSuccess:
      return {
        ...state,
        pettyCashDifference: remoteResourceLoadSuccess(action.pettyCashDifference),
      };
    case ActionType.findPettyCashDifferenceFailure:
      return {
        ...state,
        pettyCashDifference: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findPettyCashDifferenceByPaymentTypeRequested:
      return {
        ...state,
        pettyCashDifferenceByPaymentType: remoteResourceLoading([]),
      };
    case ActionType.findPettyCashDifferenceByPaymentTypeSuccess:
      return {
        ...state,
        pettyCashDifferenceByPaymentType: remoteResourceLoadSuccess(
          action.pettyCashDifferenceByPaymentType
        ),
      };
    case ActionType.findPettyCashDifferenceByPaymentTypeFailure:
      return {
        ...state,
        pettyCashDifferenceByPaymentType: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findEconomicAccounitngReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        economicAccountingReport: remoteResourceLoading([]),
      };
    case ActionType.findEconomicAccounitngReportSuccess:
      return {
        ...state,
        collectionsEconomicAccountingFilters: {
          ...state.collectionsEconomicAccountingFilters,
          groupBy: [...state.collectionsEconomicAccountingFilters.groupBy, 'countryAcronym'],
        },
        economicAccountingReport: remoteResourceLoadSuccess(action.economicAccountingReport),
      };
    case ActionType.findEconomicAccounitngReportFailure:
      return {
        ...state,
        collectionsEconomicAccountingFilters: {
          ...state.collectionsEconomicAccountingFilters,
          groupBy: [...state.collectionsEconomicAccountingFilters.groupBy, 'countryAcronym'],
        },
        economicAccountingReport: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findCashMovementReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        cashMovementReport: remoteResourceLoading([]),
      };
    case ActionType.findCashMovementReportSuccess:
      return {
        ...state,
        cashMovementReport: remoteResourceLoadSuccess(action.cashMovementReport),
      };
    case ActionType.findCashMovementReportFailure:
      return {
        ...state,
        cashMovementReport: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findCashCountReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        cashCountReport: remoteResourceLoading([]),
      };
    case ActionType.findCashCountReportSuccess:
      return {
        ...state,
        cashCountReport: remoteResourceLoadSuccess(action.cashCountReport),
      };
    case ActionType.findCashCountReportFailure:
      return {
        ...state,
        cashCountReport: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findCashDiscrepancyReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        cashDiscrepancyReport: remoteResourceLoading([]),
      };
    case ActionType.findCashDiscrepancyReportSuccess:
      return {
        ...state,
        cashDiscrepancyReport: remoteResourceLoadSuccess(action.cashDiscrepancyReport),
      };
    case ActionType.findCashDiscrepancyReportFailure:
      return {
        ...state,
        cashDiscrepancyReport: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findExchangePurchaseReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        exchangePurchaseReport: remoteResourceLoading([]),
      };
    case ActionType.findExchangePurchaseReportSuccess:
      return {
        ...state,
        exchangePurchaseReport: remoteResourceLoadSuccess(action.exchangePurchaseReport),
      };
    case ActionType.findExchangePurchaseReportFailure:
      return {
        ...state,
        exchangePurchaseReport: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findFixedFundReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        fixedFundReport: remoteResourceLoading([]),
      };
    case ActionType.findFixedFundReportSuccess:
      return {
        ...state,
        fixedFundReport: remoteResourceLoadSuccess(action.fixedFundReport),
      };
    case ActionType.findFixedFundReportFailure:
      return {
        ...state,
        fixedFundReport: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findPettyCashWithdrawalReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        pettyCashWithdrawalReport: remoteResourceLoading([]),
      };
    case ActionType.findPettyCashWithdrawalReportSuccess:
      return {
        ...state,
        pettyCashWithdrawalReport: remoteResourceLoadSuccess(action.pettyCashWithdrawalReport),
      };
    case ActionType.findPettyCashWithdrawalReportFailure:
      return {
        ...state,
        pettyCashWithdrawalReport: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findExtraCashReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        extraCashReport: remoteResourceLoading([]),
      };
    case ActionType.findExtraCashReportSuccess:
      return {
        ...state,
        extraCashReport: remoteResourceLoadSuccess(action.extraCashReport),
      };
    case ActionType.findExtraCashReportFailure:
      return {
        ...state,
        extraCashReport: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findRecoveryReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        recoveryReport: remoteResourceLoading([]),
      };
    case ActionType.findRecoveryReportSuccess:
      return {
        ...state,
        recoveryReport: remoteResourceLoadSuccess(action.recoveryReport),
      };
    case ActionType.findRecoveryReportFailure:
      return {
        ...state,
        recoveryReport: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findEnvelopeDetailsReportRequested:
      return {
        ...state,
        collectionsFilters: {
          ...state.collectionsFilters,
        },
        envelopeDetailsReport: remoteResourceLoading([]),
      };
    case ActionType.findEnvelopeDetailsReportSuccess:
      return {
        ...state,
        envelopeDetailsReport: remoteResourceLoadSuccess(action.envelopeDetailsReport),
      };
    case ActionType.findEnvelopeDetailsReportFailure:
      return {
        ...state,
        envelopeDetailsReport: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findSalesJournalRequested:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          gcs: remoteResourceLoading([]),
          ncs: remoteResourceLoading([]),
          fcs: remoteResourceLoading([]),
          thirdparty: remoteResourceLoading([]),
        },
      };

    case ActionType.findSalesJournalGcsSuccess:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          gcs: remoteResourceLoadSuccess(action.gcs),
        },
      };
    case ActionType.findSalesJournalGcsFailure:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          gcs: remoteResourceLoadFailure([], action.error),
        },
      };
    case ActionType.findSalesJournalNcsSuccess:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          ncs: remoteResourceLoadSuccess(action.ncs),
        },
      };
    case ActionType.findSalesJournalNcsFailure:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          ncs: remoteResourceLoadFailure([], action.error),
        },
      };
    case ActionType.findSalesJournalFcsSuccess:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          fcs: remoteResourceLoadSuccess(action.fcs),
        },
      };
    case ActionType.findSalesJournalFcsFailure:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          fcs: remoteResourceLoadFailure([], action.error),
        },
      };
    case ActionType.findSalesJournalThirdPartySuccess:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          thirdparty: remoteResourceLoadSuccess(action.thirdparty),
        },
      };
    case ActionType.findSalesJournalThirdPartyFailure:
      return {
        ...state,
        salesJournal: {
          ...state.salesJournal,
          thirdparty: remoteResourceLoadFailure([], action.error),
        },
      };
    case ActionType.changeMasterImportFilterCountry:
      return {
        ...state,
        mastersImport: remoteResourceInitialState([]),
        mastersImportFilters: {
          ...state.mastersImportFilters,
          countries: action.countries,
        },
      };
    case ActionType.changeMasterImportFilterStartImportDate:
      return {
        ...state,
        mastersImport: remoteResourceInitialState([]),
        mastersImportFilters: {
          ...state.mastersImportFilters,
          startImportDate: getDateValue(action.date),
        },
      };
    case ActionType.changeMasterImportFilterEndImportDate:
      return {
        ...state,
        mastersImport: remoteResourceInitialState([]),
        mastersImportFilters: {
          ...state.mastersImportFilters,
          endImportDate: getDateValue(action.date),
        },
      };
    case ActionType.changeMasterImportFilterState:
      return {
        ...state,
        mastersImport: remoteResourceInitialState([]),
        mastersImportFilters: {
          ...state.mastersImportFilters,
          state: action.state,
        },
      };
    case ActionType.changeClosingFilterCountry:
      return {
        ...state,
        closingFilters: {
          ...state.closingFilters,
          countries: action.countries,
          stores: [],
          franchiseTypes: [],
          federativeEntities: [],
        },
        closing: {
          ...state.closing,
          sales: remoteResourceInitialState([]),
          collections: remoteResourceInitialState([]),
          current: [],
        },
      };
    case ActionType.changeClosingFilterStore:
      return {
        ...state,
        closingFilters: {
          ...state.closingFilters,
          stores: action.stores,
        },
        closing: {
          ...state.closing,
          sales: remoteResourceInitialState([]),
          collections: remoteResourceInitialState([]),
          current: [],
        },
      };
    case ActionType.changeClosingFilterStartBusinessDate:
      return {
        ...state,
        closingFilters: {
          ...state.closingFilters,
          startBusinessDate: getDateValue(action.date),
        },
        closing: {
          ...state.closing,
          sales: remoteResourceInitialState([]),
          collections: remoteResourceInitialState([]),
          current: [],
        },
      };
    case ActionType.changeClosingFilterEndBusinessDate:
      return {
        ...state,
        closingFilters: {
          ...state.closingFilters,
          endBusinessDate: getDateValue(action.date),
        },
        closing: {
          ...state.closing,
          sales: remoteResourceInitialState([]),
          collections: remoteResourceInitialState([]),
          current: [],
        },
      };
    case ActionType.changeClosingFilterViewMode:
      return {
        ...state,
        closingFilters: {
          ...state.closingFilters,
          viewMode: action.viewMode,
        },
        closing: {
          ...state.closing,
          sales: remoteResourceInitialState([]),
          collections: remoteResourceInitialState([]),
          current: [],
        },
      };
    case ActionType.changeClosingFilterFederativeEntities:
      return {
        ...state,
        closingFilters: {
          ...state.closingFilters,
          federativeEntities: action.federativeEntities,
        },
        closing: {
          ...state.closing,
          sales: remoteResourceInitialState([]),
          collections: remoteResourceInitialState([]),
          current: [],
        },
      };
    case ActionType.changeFiltersOnlyDifferences:
      return {
        ...state,
        economicAccountingReport: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          onlyDifferences: action.onlyDifferences,
        },
      };
    case ActionType.changeClosingFilterFranchiseTypes:
      return {
        ...state,
        closingFilters: {
          ...state.closingFilters,
          franchiseTypes: action.franchiseTypes,
        },
        closing: {
          ...state.closing,
          sales: remoteResourceInitialState([]),
          collections: remoteResourceInitialState([]),
          current: [],
        },
      };
    case ActionType.changeReportFilterFranchiseType:
      return {
        ...state,
        economicAccountingReport: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          franchiseType: action.franchiseType,
        },
      };
    case ActionType.changeClosingFilterCauseTypes:
      return {
        ...state,
        closingFilters: {
          ...state.closingFilters,
          causeTypes: action.causeTypes,
        },
        closing: {
          ...state.closing,
          sales: remoteResourceInitialState([]),
          collections: remoteResourceInitialState([]),
          current: [],
        },
      };
    case ActionType.findMastersImportRequested:
      return {
        ...state,
        mastersImport: remoteResourceLoading([]),
      };
    case ActionType.findMastersImportSuccess:
      return {
        ...state,
        mastersImport: remoteResourceLoadSuccess(action.mastersImport),
      };
    case ActionType.findMastersImportFailure:
      return {
        ...state,
        mastersImport: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.findMastersTypesRequested:
      return {
        ...state,
        mastersTypes: remoteResourceLoading([]),
      };
    case ActionType.findMastersTypesSuccess:
      return {
        ...state,
        mastersTypes: remoteResourceLoadSuccess(action.mastersTypes),
      };
    case ActionType.findMastersTypesFailure:
      return {
        ...state,
        mastersTypes: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.mastersImportExecutionRequested:
      return {
        ...state,
        mastersImportExecution: remoteRequestLoading,
      };
    case ActionType.mastersImportExecutionSuccess:
      return {
        ...state,
        mastersImportExecution: remoteRequestSuccess,
      };
    case ActionType.mastersImportExecutionFailure:
      return {
        ...state,
        mastersImportExecution: remoteRequestFailure(action.error),
      };
    case ActionType.changeFiltersAccountingEntriesFields:
      return {
        ...state,
        sales: remoteResourceInitialState([]),
        filters: {
          ...state.filters,
          accountingEntriesFields: !state.filters.accountingEntriesFields,
        },
      };
    case ActionType.changeInterfacesFilterCountry:
      return {
        ...state,
        interfacesFilter: {
          ...state.interfacesFilter,
          country: action.country,
          stores: [],
        },
        interfaces: remoteResourceInitialState([]),
      };
    case ActionType.changeInterfacesFilterStore:
      return {
        ...state,
        interfacesFilter: {
          ...state.interfacesFilter,
          stores: action.stores,
        },
        interfaces: remoteResourceInitialState([]),
      };
    case ActionType.changeInterfacesFilterStartDate:
      return {
        ...state,
        interfacesFilter: {
          ...state.interfacesFilter,
          startDate: getDateValue(action.date),
        },
        interfaces: remoteResourceInitialState([]),
      };
    case ActionType.changeInterfacesFilterEndDate:
      return {
        ...state,
        interfacesFilter: {
          ...state.interfacesFilter,
          endDate: getDateValue(action.date),
        },
        interfaces: remoteResourceInitialState([]),
      };
    case ActionType.changeInterfacesFilterStatus:
      return {
        ...state,
        interfacesFilter: {
          ...state.interfacesFilter,
          status: action.status,
        },
        interfaces: remoteResourceInitialState([]),
      };
    case ActionType.changeInterfacesFilterInterfaceName:
      return {
        ...state,
        interfacesFilter: {
          ...state.interfacesFilter,
          interfaceName: action.interfaceName,
        },
        interfaces: remoteResourceInitialState([]),
      };
    case ActionType.findInterfacesParametersRequested:
      return {
        ...state,
        interfacesParameters: remoteResourceLoading(undefined),
      };
    case ActionType.findInterfacesParametersSuccess:
      return {
        ...state,
        interfacesParameters: remoteResourceLoadSuccess(action.parameters),
      };
    case ActionType.findInterfacesParametersFailure:
      return {
        ...state,
        interfacesParameters: remoteResourceLoadFailure(undefined, action.error),
      };
    case ActionType.findInterfacesRequested:
      return {
        ...state,
        interfaces: remoteResourceLoading([]),
      };
    case ActionType.findInterfacesSuccess:
      return {
        ...state,
        interfaces: remoteResourceLoadSuccess(action.interfaces),
      };
    case ActionType.findInterfacesFailure:
      return {
        ...state,
        interfaces: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.changePendingInterfacesFilterViewMode:
      return {
        ...state,
        pendingInterfacesFilter: {
          ...state.pendingInterfacesFilter,
        },
        pendingInterfacesState: {
          ...state.pendingInterfacesState,
          sales: remoteResourceInitialState([]),
        },
      };
    case ActionType.changePendingInterfacesFilterCountry:
      return {
        ...state,
        pendingInterfacesFilter: {
          ...state.pendingInterfacesFilter,
          country: action.country,
          stores: [],
        },
        pendingInterfacesState: {
          ...state.pendingInterfacesState,
          sales: remoteResourceInitialState([]),
        },
      };
    case ActionType.changePendingInterfacesFilterStore:
      return {
        ...state,
        pendingInterfacesFilter: {
          ...state.pendingInterfacesFilter,
          stores: action.stores,
        },
        pendingInterfacesState: {
          ...state.pendingInterfacesState,
          sales: remoteResourceInitialState([]),
        },
      };
    case ActionType.changePendingInterfacesFilterEndBusinessDate:
      return {
        ...state,
        pendingInterfacesFilter: {
          ...state.pendingInterfacesFilter,
          endBusinessDate: getDateValue(action.date),
        },
        pendingInterfacesState: {
          ...state.pendingInterfacesState,
          sales: remoteResourceInitialState([]),
        },
      };
    case ActionType.changePendingInterfacesFilterStartBusinessDate:
      return {
        ...state,
        pendingInterfacesFilter: {
          ...state.pendingInterfacesFilter,
          startBusinessDate: getDateValue(action.date),
        },
        pendingInterfacesState: {
          ...state.pendingInterfacesState,
          sales: remoteResourceInitialState([]),
        },
      };
    case ActionType.changePendingInterfacesFilterQuery:
      return {
        ...state,
        pendingInterfacesFilter: {
          ...state.pendingInterfacesFilter,
          queryFilter: action.filter,
        },
        pendingInterfacesState: {
          ...state.pendingInterfacesState,
          sales: remoteResourceInitialState([]),
        },
      };
    case ActionType.findPendingInterfacesSalesSuccess:
      return {
        ...state,
        pendingInterfacesState: {
          ...state.pendingInterfacesState,
          ...{
            sales: remoteResourceLoadSuccess(action.pendingInterfaces),
          },
        },
      };
    case ActionType.findPendingInterfacesSalesFailure:
      return {
        ...state,
        pendingInterfacesState: {
          ...state.pendingInterfacesState,
          ...{ sales: remoteResourceLoadFailure([], action.error) },
        },
      };
    case ActionType.changeExecutePendingInterfacesFilterCountry:
      return {
        ...state,
        executionStatusPendingInterfacesFilter: {
          ...state.executionStatusPendingInterfacesFilter,
          country: action.country,
          stores: [],
        },
        executionStatusPendingInterfacesState: {
          ...state.executionStatusPendingInterfacesState,
          pendingInterfacesStates: remoteResourceInitialState([]),
        },
      };
    case ActionType.changeExecutePendingInterfacesFilterStore:
      return {
        ...state,
        executionStatusPendingInterfacesFilter: {
          ...state.executionStatusPendingInterfacesFilter,
          stores: action.stores,
        },
        executionStatusPendingInterfacesState: {
          ...state.executionStatusPendingInterfacesState,
          pendingInterfacesStates: remoteResourceInitialState([]),
        },
      };
    case ActionType.changeExecutePendingInterfacesFilterBusinessDate:
      return {
        ...state,
        executionStatusPendingInterfacesFilter: {
          ...state.executionStatusPendingInterfacesFilter,
          businessDate: getDateValue(action.date),
        },
        executionStatusPendingInterfacesState: {
          ...state.executionStatusPendingInterfacesState,
          pendingInterfacesStates: remoteResourceInitialState([]),
        },
      };
    case ActionType.changeExecutePendingInterfacesFilterStatus:
      return {
        ...state,
        executionStatusPendingInterfacesFilter: {
          ...state.executionStatusPendingInterfacesFilter,
          status: action.status,
        },
        executionStatusPendingInterfacesState: {
          ...state.executionStatusPendingInterfacesState,
          pendingInterfacesStates: remoteResourceInitialState([]),
        },
      };
    case ActionType.executePendingInterfacesSuccess:
      return {
        ...state,
        executionStatusPendingInterfacesState: {
          ...state.executionStatusPendingInterfacesState,
          ...{
            pendingInterfacesStates: remoteResourceLoadSuccess(
              action.executionStatusPendingInterfaces
            ),
          },
        },
      };
    case ActionType.executePendingInterfacesFailure:
      return {
        ...state,
        executionStatusPendingInterfacesState: {
          ...state.executionStatusPendingInterfacesState,
          ...{ pendingInterfacesStates: remoteResourceLoadFailure([], action.error) },
        },
      };
    case ActionType.executePendingInterfacesParametersRequested:
      return {
        ...state,
        parameters: remoteResourceLoading(undefined),
      };
    case ActionType.executePendingInterfacesParametersSuccess:
      return {
        ...state,
        parameters: remoteResourceLoadSuccess(action.parameters),
      };
    case ActionType.executePendingInterfacesParametersFailure:
      return {
        ...state,
        parameters: remoteResourceLoadFailure(undefined, action.error),
      };
    default:
      return state;
  }
};

export default reducer;
